import { h, createContext, ComponentChildren } from "preact"; /** @jsx h */
import { useContext, useState, useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import { ROUTES } from '@pyqml/shared';
import { Page, Author, Category, Paywalled } from '@pyqml/model';

import { useEnvironment } from "../app/Environment";
import { useCacheableQueryProvider } from "../app/CacheableQueryProvider";
import { useAuth } from "../app/AuthProvider";

export type IArticleProps = {
    children?: ((article: Page.IPage, paywalled: Paywalled.IPaywalled) => ComponentChildren);
    draft: boolean | undefined;
    usePassage: boolean | undefined;
};

const ArticleContext = createContext<any>(undefined);

export const ArticleProvider = (props: IArticleProps)  => {

    const { subdomain, isDevelopment } = useEnvironment();
    const { cacheableQuery } = useCacheableQueryProvider();
    const { authQuery, token } = useAuth();
    const location = useLocation();

    // maybe there is a email-token in the query
    const urlParams = new URLSearchParams(window.location.search);
    const queryEmtkn = urlParams.get("emtkn");
    const queryLitkn = urlParams.get("litkn"); // "2025-03-05-10-30-09-049"; 
    
    const slug = (function () {
        const temp = location.path.replace(ROUTES.BLOG_DRAFT, '').replace(`${ROUTES.BLOG_WIKI}`, '');

        return temp.replace(/^\/+/, '');
    })();


    const parts = slug.split('/');
    
    console.log("ArticleProvider: ", slug, parts);

    const [mainContent, refetch, count] = cacheableQuery?.({
        method: "POST",
        endpoint: props.usePassage ? ROUTES.CLOUDAPI_PUBLIC_GET_PUBLISHED_PASSAGE : ROUTES.CLOUDAPI_PUBLIC_GET_PUBLISHED_PAGE,
        queryData: {
            subdomain: subdomain,
            slug: parts.length > 1 ? parts[1] : parts[0], // the second part of the parameters
            wikiSlug: parts[0], // the first part of the parameters
            draft: props.draft
        },
        reloadOn: [subdomain, location.path]
    });

    const [paywalledWithToken, paywallRefetchWithToken, paywallCountWithToken] = authQuery?.({
        method: "POST",
        endpoint: ROUTES.CLOUDAPI_PUBLIC_GET_PAYWALLED_PAGE_WITH_EMAILTOKEN,
        queryData: {
            subdomain: subdomain,
            slug: slug,
            queryEmtkn: queryEmtkn,
            queryLitkn: queryLitkn,
        },
        reloadOn: [subdomain, location.path],
        manual: (!queryEmtkn && !queryLitkn) //|| !loaded // when there is no queryEmtkn, we don't fetch the paywalled content this way
    });


    const [paywalled, paywallRefetch, paywallCount] = authQuery?.({
        method: "POST",
        endpoint: ROUTES.CLOUDAPI_PRIVATE_GET_PAYWALLED_PAGE,
        queryData: {
            subdomain: subdomain,
            slug: parts.length > 1 ? parts[1] : parts[0],
            draft: props.draft
        },
        reloadOn: [subdomain, location.path],
        manual: !token //|| !loaded// when there is no token, we don't fetch the paywalled content
    });


    //console.log("ArticleProvider: ",paywalled)

    //console.log("ArticleProvider: ", data, loading, error, count);
    
    /*
    const data: Page.IPage & Author.IAuthor & Category.ICategory = { 
        title: "This is the title",
        text: "A brief abstract to the article",
        categoryTitle: "stories",
        categoryLink: "./stories",
        image: HomeImg,
        avatar: HomeImg,
        authorLinkTo: "./author.html", //derive from nickname
        name: "Frank Zickert",
        publishedAt: "Dec 12", // derive from revision
        readingTime: "5"
    };*/

    //console.log("Article", data, loading, error, count);

    const paywalledData = paywalled?.data ? paywalled.data : paywalledWithToken?.data;
    //console.log("ArticleProvider: ", paywalledData);

    return <ArticleContext.Provider value={{
        mainContent: mainContent?.data ?? undefined,
        paywalled: paywalledData
    }}>
        {props.children?.(mainContent?.data, paywalledData)}
    </ArticleContext.Provider>
};
    

export const useBlog: any = () => {
    return useContext(ArticleContext);
};